// src/pages/Scheduler.js
'use strict';

import React, { useEffect, useMemo, useState } from 'react';
import api from '../api/api';

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import {
  Add as AddIcon,
  Block as DisableIcon,
  CheckCircle as EnableIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ListAlt as RunsIcon,
  PlayArrow as RunNowIcon,
  Refresh as RefreshIcon,
  RestartAlt as RestartIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

function parseJsonSafe(value, fallback = {}) {
  if (value == null || value === '') return fallback;
  if (typeof value === 'object') return value;

  try {
    return JSON.parse(String(value));
  } catch (_) {
    return fallback;
  }
}

function normalizePositiveInt(value, fallback, min = 1, max = 100000) {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(min, Math.min(Math.trunc(n), max));
}

function normalizeJobType(raw) {
  if (!raw || typeof raw !== 'object') return null;

  const jobKey = String(
    raw.job_key ??
    raw.jobKey ??
    raw.value ??
    raw.key ??
    ''
  ).trim();

  if (!jobKey) return null;

  const label = String(
    raw.label ??
    raw.name ??
    raw.defaultName ??
    raw.default_name ??
    jobKey
  ).trim();

  const description = String(raw.description ?? raw.descripcion ?? '').trim();

  const defaultEveryMinutes = normalizePositiveInt(
    raw.defaultEveryMinutes ??
    raw.default_every_minutes ??
    raw.every_minutes,
    5,
    1,
    525600
  );

  const defaultPriority = normalizePositiveInt(
    raw.defaultPriority ??
    raw.default_priority ??
    raw.priority,
    100,
    1,
    100000
  );

  const scopesRaw =
    raw.tenantScopes ??
    raw.tenant_scopes ??
    raw.scopes ??
    ['single', 'all'];

  const tenantScopes = Array.isArray(scopesRaw)
    ? scopesRaw
        .map((x) => String(x || '').trim())
        .filter((x) => x === 'single' || x === 'all')
    : ['single', 'all'];

  const defaultParams = parseJsonSafe(
    raw.defaultParams ??
    raw.default_params ??
    raw.params,
    {}
  );

  return {
    value: jobKey,
    job_key: jobKey,
    label: label || jobKey,
    description,
    defaultName: String(raw.defaultName ?? raw.default_name ?? label ?? jobKey).trim(),
    defaultEveryMinutes,
    defaultPriority,
    tenantScopes: tenantScopes.length ? tenantScopes : ['single', 'all'],
    defaultParams:
      defaultParams && typeof defaultParams === 'object' && !Array.isArray(defaultParams)
        ? defaultParams
        : {},
    worker: raw.worker || raw.task || raw.task_key || null,
  };
}

function catalogJobTypes(catalog) {
  const candidates = [
    catalog?.jobTypes,
    catalog?.job_types,
    catalog?.types,
  ];

  for (const value of candidates) {
    if (Array.isArray(value)) {
      return value.map(normalizeJobType).filter(Boolean);
    }
  }

  return [];
}

function mergeJobTypes(catalogTypes, jobs) {
  const map = new Map();

  for (const item of catalogTypes || []) {
    if (item?.value) map.set(item.value, item);
  }

  // Compatibilidad: si existe un job antiguo que ya no viene en el catálogo,
  // igualmente puede visualizarse y editarse sin romper la pantalla.
  for (const job of jobs || []) {
    const key = String(job?.job_key || '').trim();
    if (!key || map.has(key)) continue;

    map.set(key, {
      value: key,
      job_key: key,
      label: job?.name || key,
      description: 'Trabajo existente no informado actualmente por el catálogo del backend.',
      defaultName: job?.name || key,
      defaultEveryMinutes: normalizePositiveInt(job?.every_minutes, 5),
      defaultPriority: normalizePositiveInt(job?.priority, 100),
      tenantScopes: ['single', 'all'],
      defaultParams: parseJsonSafe(job?.params, {}),
      worker: null,
      legacy: true,
    });
  }

  return Array.from(map.values()).sort((a, b) =>
    String(a.label).localeCompare(String(b.label), 'es')
  );
}

function getJobType(jobTypes, jobKey) {
  return (jobTypes || []).find((x) => x.value === jobKey) || null;
}

function paramsToText(params) {
  if (!params || typeof params !== 'object' || Array.isArray(params)) return '';
  if (!Object.keys(params).length) return '';
  return JSON.stringify(params, null, 2);
}

function buildDefaultForm(jobType) {
  const cfg = jobType || null;
  const scopes = cfg?.tenantScopes || ['single', 'all'];
  const defaultScope = scopes.includes('single') ? 'single' : (scopes[0] || 'all');

  return {
    name: cfg?.defaultName || cfg?.label || '',
    job_key: cfg?.value || '',
    enabled: true,
    tenant_scope: defaultScope,
    tenant_id: '',
    every_minutes: cfg?.defaultEveryMinutes || 5,
    priority: cfg?.defaultPriority || 100,
    params_json: paramsToText(cfg?.defaultParams || {}),
  };
}

function buildFormFromJob(job, jobTypes) {
  const cfg = getJobType(jobTypes, job?.job_key);
  const params = parseJsonSafe(job?.params, {});

  return {
    name: job?.name || cfg?.defaultName || cfg?.label || job?.job_key || '',
    job_key: job?.job_key || cfg?.value || '',
    enabled: !!job?.enabled,
    tenant_scope: job?.tenant_scope || 'single',
    tenant_id: job?.tenant_id || '',
    every_minutes: normalizePositiveInt(
      job?.every_minutes,
      cfg?.defaultEveryMinutes || 5,
      1,
      525600
    ),
    priority: normalizePositiveInt(
      job?.priority,
      cfg?.defaultPriority || 100,
      1,
      100000
    ),
    params_json: paramsToText(params),
  };
}

function buildPayloadParams(form) {
  const raw = String(form?.params_json || '').trim();
  if (!raw) return {};

  const parsed = JSON.parse(raw);
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error('Los parámetros deben ser un objeto JSON.');
  }

  return parsed;
}

function formatDateTime(value) {
  if (!value) return '-';

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);

  return d.toLocaleString('es-CL', {
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

function formatSchedule(job) {
  const params = parseJsonSafe(job?.params, {});
  const localTime = String(
    params?.hora_local ??
    params?.local_time ??
    params?.time ??
    ''
  ).trim();

  if (localTime) {
    return {
      primary: `Diario · ${localTime}`,
      secondary: String(params?.timezone || '').trim() || null,
    };
  }

  const every = Number(job?.every_minutes || 0);
  if (!Number.isFinite(every) || every <= 0) {
    return { primary: '-', secondary: null };
  }

  if (every === 1) return { primary: 'Cada minuto', secondary: null };
  if (every < 60) return { primary: `Cada ${every} min`, secondary: null };
  if (every === 60) return { primary: 'Cada hora', secondary: null };
  if (every % 1440 === 0) {
    const days = every / 1440;
    return {
      primary: days === 1 ? 'Cada día' : `Cada ${days} días`,
      secondary: null,
    };
  }
  if (every % 60 === 0) return { primary: `Cada ${every / 60} h`, secondary: null };

  return { primary: `Cada ${every} min`, secondary: null };
}

function getJobVisualState(job) {
  if (job?.locked_at) {
    return { label: 'Ejecutando', color: 'warning' };
  }
  if (job?.enabled) {
    return { label: 'Activo', color: 'success' };
  }
  return { label: 'Pausado', color: 'default' };
}

function getRunColor(status) {
  const value = String(status || '').toLowerCase();
  if (['ok', 'success', 'completed', 'completado'].includes(value)) return 'success';
  if (['error', 'failed', 'fallido'].includes(value)) return 'error';
  if (['running', 'started', 'ejecutando'].includes(value)) return 'warning';
  return 'default';
}

// -----------------------------------------------------------------------------
// Componente
// -----------------------------------------------------------------------------

export default function Scheduler() {
  const [controlLoading, setControlLoading] = useState(false);
  const [workerNode, setWorkerNode] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [workerCatalog, setWorkerCatalog] = useState(null);

  const [jobsLoading, setJobsLoading] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, limit: 20 });

  const [search, setSearch] = useState('');
  const [enabledFilter, setEnabledFilter] = useState('');
  const [jobKeyFilter, setJobKeyFilter] = useState('');

  const [tenants, setTenants] = useState([]);

  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [form, setForm] = useState(buildDefaultForm(null));

  const [openRuns, setOpenRuns] = useState(false);
  const [runsLoading, setRunsLoading] = useState(false);
  const [runs, setRuns] = useState([]);
  const [runsJob, setRunsJob] = useState(null);

  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: 'success',
    message: '',
  });

  const showSnack = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const backendJobTypes = useMemo(
    () => catalogJobTypes(workerCatalog),
    [workerCatalog]
  );

  const jobTypes = useMemo(
    () => mergeJobTypes(backendJobTypes, jobs),
    [backendJobTypes, jobs]
  );

  const currentJobType = useMemo(
    () => getJobType(jobTypes, form.job_key),
    [jobTypes, form.job_key]
  );

  const tenantOptions = useMemo(() => {
    return (tenants || []).map((t) => ({
      id: t.id,
      name: t.name || t.razon_social || t.db_name || t.id,
      db: t.db_name,
    }));
  }, [tenants]);

  const tenantNameById = useMemo(() => {
    const map = new Map();
    for (const t of tenantOptions) map.set(t.id, t.name || t.db || t.id);
    return map;
  }, [tenantOptions]);

  const schedulerWorker = useMemo(() => {
    return (workers || []).find((w) =>
      w?.key === 'scheduler' ||
      w?.meta?.role === 'scheduler' ||
      String(w?.label || '').toLowerCase().includes('scheduler')
    ) || workers?.[0] || null;
  }, [workers]);

  const schedulerRunning = schedulerWorker?.state === 'running';

  // ---------------------------------------------------------------------------
  // Fetch
  // ---------------------------------------------------------------------------

  const fetchControlPlane = async () => {
    setControlLoading(true);

    try {
      const [workersRes, catalogRes] = await Promise.all([
        api.get('/scheduler/workers'),
        api.get('/scheduler/catalog'),
      ]);

      setWorkerNode(workersRes.data?.node || null);
      setWorkers(Array.isArray(workersRes.data?.workers) ? workersRes.data.workers : []);
      setWorkerCatalog(catalogRes.data || null);
    } catch (err) {
      console.error(err);
      setWorkerNode(null);
      setWorkers([]);
      setWorkerCatalog(null);
      showSnack(
        err?.response?.data?.mensaje ||
        err?.response?.data?.message ||
        'No fue posible consultar worker-01',
        'error'
      );
    } finally {
      setControlLoading(false);
    }
  };

  const fetchTenants = async () => {
    try {
      const res = await api.get('/tenants', {
        params: { page: 1, limit: 500 },
      });
      setTenants(Array.isArray(res?.data?.tenants) ? res.data.tenants : []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchJobs = async (page = meta.page) => {
    setJobsLoading(true);

    try {
      const params = {
        page,
        limit: meta.limit,
      };

      if (search.trim()) params.search = search.trim();
      if (enabledFilter !== '') params.enabled = enabledFilter;
      if (jobKeyFilter) params.job_key = jobKeyFilter;

      const res = await api.get('/scheduler/jobs', { params });
      setJobs(Array.isArray(res.data?.jobs) ? res.data.jobs : []);
      setMeta(res.data?.meta || { total: 0, page, limit: meta.limit });
    } catch (err) {
      console.error(err);
      showSnack('Error cargando trabajos programados', 'error');
    } finally {
      setJobsLoading(false);
    }
  };

  const refreshAll = async () => {
    await Promise.allSettled([
      fetchControlPlane(),
      fetchTenants(),
      fetchJobs(meta.page),
    ]);
  };

  useEffect(() => {
    fetchControlPlane();
    fetchTenants();
    fetchJobs(1);
  }, []);

  // ---------------------------------------------------------------------------
  // Acciones worker / scheduler
  // ---------------------------------------------------------------------------

  const restartScheduler = async () => {
    const key = schedulerWorker?.key || 'scheduler';
    setControlLoading(true);

    try {
      await api.post(`/scheduler/workers/${encodeURIComponent(key)}/restart`);
      await fetchControlPlane();
      showSnack('Scheduler reiniciado correctamente', 'success');
    } catch (err) {
      console.error(err);
      showSnack(
        err?.response?.data?.mensaje ||
        err?.response?.data?.message ||
        'No fue posible reiniciar el scheduler',
        'error'
      );
    } finally {
      setControlLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Formulario
  // ---------------------------------------------------------------------------

  const openCreate = () => {
    const firstType = jobTypes[0] || null;

    if (!firstType) {
      return showSnack(
        'El backend no informó tipos de trabajo en /scheduler/catalog.',
        'error'
      );
    }

    setEditing(null);
    setShowAdvanced(false);
    setForm(buildDefaultForm(firstType));
    setOpenForm(true);
  };

  const openEdit = async (job) => {
    setFormLoading(true);
    setEditing(job);
    setShowAdvanced(false);
    setOpenForm(true);

    try {
      const res = await api.get(`/scheduler/jobs/${job.id}`);
      const fullJob = res?.data?.job || job;
      setEditing(fullJob);
      setForm(buildFormFromJob(fullJob, jobTypes));
    } catch (err) {
      console.error(err);
      setForm(buildFormFromJob(job, jobTypes));
      showSnack('Se cargó la información disponible del trabajo.', 'warning');
    } finally {
      setFormLoading(false);
    }
  };

  const closeForm = () => {
    setOpenForm(false);
    setEditing(null);
    setShowAdvanced(false);
  };

  const handleJobTypeChange = (jobKey) => {
    const cfg = getJobType(jobTypes, jobKey);
    if (!cfg) return;

    const defaults = buildDefaultForm(cfg);

    setForm((prev) => ({
      ...defaults,
      // Al editar conservamos nombre, frecuencia y scope actuales salvo que el
      // usuario cambie esos campos explícitamente.
      name: editing?.id ? prev.name : defaults.name,
      enabled: editing?.id ? prev.enabled : defaults.enabled,
      tenant_scope: editing?.id ? prev.tenant_scope : defaults.tenant_scope,
      tenant_id: editing?.id ? prev.tenant_id : defaults.tenant_id,
      every_minutes: editing?.id ? prev.every_minutes : defaults.every_minutes,
      priority: editing?.id ? prev.priority : defaults.priority,
      job_key: jobKey,
      params_json: editing?.id ? prev.params_json : defaults.params_json,
    }));
  };

  const saveJob = async () => {
    const name = String(form.name || '').trim();
    const jobKey = String(form.job_key || '').trim();

    if (!name) return showSnack('Ingresa un nombre para el trabajo.', 'error');
    if (!jobKey) return showSnack('Selecciona un tipo de trabajo.', 'error');

    const everyMinutes = Number(form.every_minutes);
    if (!Number.isFinite(everyMinutes) || everyMinutes <= 0) {
      return showSnack('La frecuencia debe ser mayor que cero.', 'error');
    }

    if (form.tenant_scope === 'single' && !form.tenant_id) {
      return showSnack('Selecciona la empresa para este trabajo.', 'error');
    }

    let paramsPayload;
    try {
      paramsPayload = buildPayloadParams(form);
    } catch (err) {
      console.error(err);
      setShowAdvanced(true);
      return showSnack(
        err?.message || 'La configuración avanzada contiene JSON inválido.',
        'error'
      );
    }

    const payload = {
      name,
      job_key: jobKey,
      enabled: !!form.enabled,
      tenant_scope: form.tenant_scope,
      tenant_id: form.tenant_scope === 'single' ? form.tenant_id : null,
      every_minutes: normalizePositiveInt(everyMinutes, 5, 1, 525600),
      priority: normalizePositiveInt(form.priority, 100, 1, 100000),
      params: paramsPayload,
    };

    setFormLoading(true);

    try {
      if (editing?.id) {
        await api.put(`/scheduler/jobs/${editing.id}`, payload);
        showSnack('Trabajo actualizado', 'success');
      } else {
        await api.post('/scheduler/jobs', payload);
        showSnack('Trabajo creado', 'success');
      }

      closeForm();
      await fetchJobs(1);
    } catch (err) {
      console.error(err);
      showSnack(
        err?.response?.data?.mensaje ||
        err?.response?.data?.message ||
        'Error guardando el trabajo',
        'error'
      );
    } finally {
      setFormLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Acciones jobs
  // ---------------------------------------------------------------------------

  const deleteJob = async (job) => {
    if (!window.confirm(`¿Eliminar "${job?.name}"?`)) return;

    try {
      await api.delete(`/scheduler/jobs/${job.id}`);
      showSnack('Trabajo eliminado', 'info');
      await fetchJobs(meta.page);
    } catch (err) {
      console.error(err);
      showSnack('Error eliminando el trabajo', 'error');
    }
  };

  const runNow = async (job) => {
    try {
      await api.post(`/scheduler/jobs/${job.id}/run-now`);
      showSnack('Trabajo enviado a ejecución', 'success');
      await fetchJobs(meta.page);
    } catch (err) {
      console.error(err);
      showSnack(
        err?.response?.data?.mensaje ||
        err?.response?.data?.message ||
        'Error solicitando la ejecución',
        'error'
      );
    }
  };

  const setEnabled = async (job, enabled) => {
    try {
      await api.post(`/scheduler/jobs/${job.id}/${enabled ? 'enable' : 'disable'}`);
      showSnack(enabled ? 'Trabajo habilitado' : 'Trabajo pausado', 'success');
      await fetchJobs(meta.page);
    } catch (err) {
      console.error(err);
      showSnack('Error cambiando el estado del trabajo', 'error');
    }
  };

  const openRunsDialog = async (job) => {
    setRunsJob(job);
    setOpenRuns(true);
    setRuns([]);
    setRunsLoading(true);

    try {
      const res = await api.get(`/scheduler/jobs/${job.id}/runs`, {
        params: { limit: 50 },
      });
      setRuns(Array.isArray(res.data?.runs) ? res.data.runs : []);
    } catch (err) {
      console.error(err);
      showSnack('Error cargando el historial', 'error');
    } finally {
      setRunsLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack spacing={2.25}>
        {/* Encabezado */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          gap={2}
          flexWrap="wrap"
        >
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 900 }}>
              Automatizaciones
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4 }}>
              Trabajos automáticos ejecutados por Worker-01.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title="Actualizar">
              <span>
                <IconButton
                  onClick={refreshAll}
                  disabled={controlLoading || jobsLoading}
                >
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreate}
              disabled={!jobTypes.length || controlLoading}
            >
              Nuevo trabajo
            </Button>
          </Stack>
        </Box>

        {/* Estado compacto de Worker-01 */}
        <Paper variant="outlined" sx={{ p: 1.75 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            gap={2}
            flexWrap="wrap"
          >
            <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap">
              <Chip
                size="small"
                color={workerNode ? 'success' : 'error'}
                label={workerNode ? 'Worker-01 conectado' : 'Worker-01 sin conexión'}
              />

              <Chip
                size="small"
                color={schedulerRunning ? 'success' : 'warning'}
                variant="outlined"
                label={schedulerRunning ? 'Scheduler activo' : 'Scheduler detenido'}
              />

              {workerNode && (
                <Typography variant="body2" color="text.secondary">
                  {workerNode.hostname || workerNode.id || 'worker-01'}
                  {workerNode.uptimeSeconds != null
                    ? ` · ${Math.floor(Number(workerNode.uptimeSeconds || 0) / 60)} min activo`
                    : ''}
                </Typography>
              )}

              {controlLoading && <CircularProgress size={17} />}
            </Stack>

            <Button
              size="small"
              variant="text"
              startIcon={<RestartIcon />}
              onClick={restartScheduler}
              disabled={controlLoading || !schedulerWorker}
            >
              Reiniciar scheduler
            </Button>
          </Box>
        </Paper>

        {!backendJobTypes.length && !controlLoading && (
          <Alert severity="warning">
            El catálogo del backend no informó tipos de trabajo. Los trabajos existentes
            seguirán visibles, pero no se podrán crear tipos nuevos hasta que
            <b> /scheduler/catalog</b> los publique.
          </Alert>
        )}

        {/* Filtros */}
        <Paper variant="outlined" sx={{ p: 1.5 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.25}
            alignItems={{ md: 'center' }}
          >
            <TextField
              size="small"
              label="Buscar trabajo"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') fetchJobs(1);
              }}
              sx={{ minWidth: 260, flex: { md: 1 } }}
            />

            <FormControl size="small" sx={{ minWidth: 230 }}>
              <InputLabel>Tipo</InputLabel>
              <Select
                label="Tipo"
                value={jobKeyFilter}
                onChange={(e) => setJobKeyFilter(e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                {jobTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Estado</InputLabel>
              <Select
                label="Estado"
                value={enabledFilter}
                onChange={(e) => setEnabledFilter(e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="true">Activos</MenuItem>
                <MenuItem value="false">Pausados</MenuItem>
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              onClick={() => fetchJobs(1)}
              disabled={jobsLoading}
            >
              Filtrar
            </Button>
          </Stack>
        </Paper>

        {/* Lista principal */}
        <Paper sx={{ overflow: 'hidden' }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            px={2}
            py={1.75}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 850 }}>
                Trabajos programados
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {meta.total || 0} configurado(s)
              </Typography>
            </Box>
            {jobsLoading && <CircularProgress size={18} />}
          </Box>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Trabajo</TableCell>
                  <TableCell>Empresa</TableCell>
                  <TableCell>Frecuencia</TableCell>
                  <TableCell>Próxima ejecución</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {jobs.map((job) => {
                  const cfg = getJobType(jobTypes, job.job_key);
                  const schedule = formatSchedule(job);
                  const visualState = getJobVisualState(job);

                  return (
                    <TableRow key={job.id} hover>
                      <TableCell sx={{ minWidth: 260 }}>
                        <Typography variant="body2" sx={{ fontWeight: 800 }}>
                          {job.name || cfg?.label || job.job_key}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', mt: 0.2 }}
                        >
                          {cfg?.label && cfg.label !== job.name ? `${cfg.label} · ` : ''}
                          {job.job_key}
                        </Typography>
                      </TableCell>

                      <TableCell sx={{ minWidth: 190 }}>
                        {job.tenant_scope === 'all' ? (
                          <Chip size="small" variant="outlined" label="Todas las empresas" />
                        ) : (
                          <Typography variant="body2">
                            {tenantNameById.get(job.tenant_id) || job.tenant_id || '-'}
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell sx={{ minWidth: 150 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {schedule.primary}
                        </Typography>
                        {schedule.secondary && (
                          <Typography variant="caption" color="text.secondary">
                            {schedule.secondary}
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell sx={{ minWidth: 170 }}>
                        <Typography variant="body2">
                          {formatDateTime(job.next_run_at)}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Chip
                          size="small"
                          color={visualState.color}
                          label={visualState.label}
                        />
                      </TableCell>

                      <TableCell align="right" sx={{ minWidth: 205 }}>
                        <Stack direction="row" spacing={0.25} justifyContent="flex-end">
                          <Tooltip title="Historial">
                            <IconButton size="small" onClick={() => openRunsDialog(job)}>
                              <RunsIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Ejecutar ahora">
                            <IconButton size="small" onClick={() => runNow(job)}>
                              <RunNowIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title={job.enabled ? 'Pausar' : 'Habilitar'}>
                            <IconButton
                              size="small"
                              color={job.enabled ? 'warning' : 'success'}
                              onClick={() => setEnabled(job, !job.enabled)}
                            >
                              {job.enabled
                                ? <DisableIcon fontSize="small" />
                                : <EnableIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Editar">
                            <IconButton size="small" onClick={() => openEdit(job)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Eliminar">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => deleteJob(job)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}

                {!jobs.length && !jobsLoading && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Box py={4} textAlign="center">
                        <Typography variant="body2" color="text.secondary">
                          No hay trabajos para mostrar.
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            px={2}
            py={1.5}
            gap={1}
            flexWrap="wrap"
          >
            <Typography variant="caption" color="text.secondary">
              Página {meta.page || 1}
            </Typography>

            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                disabled={(meta.page || 1) <= 1 || jobsLoading}
                onClick={() => fetchJobs((meta.page || 1) - 1)}
              >
                Anterior
              </Button>
              <Button
                size="small"
                disabled={
                  ((meta.page || 1) * (meta.limit || 20)) >= (meta.total || 0) ||
                  jobsLoading
                }
                onClick={() => fetchJobs((meta.page || 1) + 1)}
              >
                Siguiente
              </Button>
            </Stack>
          </Box>
        </Paper>
      </Stack>

      {/* Crear / editar */}
      <Dialog open={openForm} onClose={closeForm} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>
          {editing?.id ? 'Editar trabajo' : 'Nuevo trabajo'}
        </DialogTitle>

        <DialogContent dividers>
          {formLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <Stack spacing={2}>
              <FormControl fullWidth>
                <InputLabel>Tipo de trabajo</InputLabel>
                <Select
                  label="Tipo de trabajo"
                  value={form.job_key}
                  onChange={(e) => handleJobTypeChange(e.target.value)}
                >
                  {jobTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {currentJobType?.description && (
                <Alert severity="info" icon={false}>
                  <Typography variant="body2">
                    {currentJobType.description}
                  </Typography>
                </Alert>
              )}

              <TextField
                label="Nombre"
                value={form.name}
                onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                fullWidth
              />

              <FormControl fullWidth>
                <InputLabel>Aplicar a</InputLabel>
                <Select
                  label="Aplicar a"
                  value={form.tenant_scope}
                  onChange={(e) => {
                    const scope = e.target.value;
                    setForm((prev) => ({
                      ...prev,
                      tenant_scope: scope,
                      tenant_id: scope === 'all' ? '' : prev.tenant_id,
                    }));
                  }}
                >
                  {(currentJobType?.tenantScopes || ['single', 'all']).includes('single') && (
                    <MenuItem value="single">Una empresa</MenuItem>
                  )}
                  {(currentJobType?.tenantScopes || ['single', 'all']).includes('all') && (
                    <MenuItem value="all">Todas las empresas</MenuItem>
                  )}
                </Select>
              </FormControl>

              {form.tenant_scope === 'single' && (
                <FormControl fullWidth>
                  <InputLabel>Empresa</InputLabel>
                  <Select
                    label="Empresa"
                    value={form.tenant_id}
                    onChange={(e) => setForm((prev) => ({
                      ...prev,
                      tenant_id: e.target.value,
                    }))}
                  >
                    <MenuItem value="">Seleccionar…</MenuItem>
                    {tenantOptions.map((tenant) => (
                      <MenuItem key={tenant.id} value={tenant.id}>
                        {tenant.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <TextField
                  label="Frecuencia (minutos)"
                  type="number"
                  value={form.every_minutes}
                  onChange={(e) => setForm((prev) => ({
                    ...prev,
                    every_minutes: e.target.value,
                  }))}
                  helperText={
                    parseJsonSafe(form.params_json, {})?.hora_local
                      ? 'El horario diario se toma de la configuración del trabajo.'
                      : 'Intervalo entre ejecuciones.'
                  }
                  fullWidth
                />

                <FormControlLabel
                  sx={{ minWidth: 135, mx: 0 }}
                  control={
                    <Switch
                      checked={!!form.enabled}
                      onChange={(e) => setForm((prev) => ({
                        ...prev,
                        enabled: e.target.checked,
                      }))}
                    />
                  }
                  label="Activo"
                />
              </Stack>

              <Button
                size="small"
                variant="text"
                startIcon={<SettingsIcon />}
                onClick={() => setShowAdvanced((v) => !v)}
                sx={{ alignSelf: 'flex-start' }}
              >
                {showAdvanced ? 'Ocultar opciones avanzadas' : 'Opciones avanzadas'}
              </Button>

              {showAdvanced && (
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Stack spacing={1.5}>
                    <TextField
                      label="Prioridad"
                      type="number"
                      value={form.priority}
                      onChange={(e) => setForm((prev) => ({
                        ...prev,
                        priority: e.target.value,
                      }))}
                      helperText="Normalmente no es necesario modificarla."
                      fullWidth
                    />

                    <TextField
                      label="Parámetros JSON"
                      value={form.params_json}
                      onChange={(e) => setForm((prev) => ({
                        ...prev,
                        params_json: e.target.value,
                      }))}
                      placeholder="{}"
                      multiline
                      minRows={5}
                      fullWidth
                      helperText="El backend define los valores por defecto. Edita sólo si este trabajo requiere una configuración especial."
                    />
                  </Stack>
                </Paper>
              )}
            </Stack>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={closeForm}>Cancelar</Button>
          <Button variant="contained" onClick={saveJob} disabled={formLoading}>
            {editing?.id ? 'Guardar cambios' : 'Crear trabajo'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Historial */}
      <Dialog
        open={openRuns}
        onClose={() => setOpenRuns(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 900 }}>
          Historial{runsJob?.name ? ` · ${runsJob.name}` : ''}
        </DialogTitle>

        <DialogContent dividers>
          {runsLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Estado</TableCell>
                    <TableCell>Empresa</TableCell>
                    <TableCell>Inicio</TableCell>
                    <TableCell>Resultado</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {runs.map((run) => (
                    <TableRow key={run.id} hover>
                      <TableCell>
                        <Chip
                          size="small"
                          label={run.status || '-'}
                          color={getRunColor(run.status)}
                        />
                      </TableCell>
                      <TableCell>
                        {tenantNameById.get(run.tenant_id) || run.tenant_id || '-'}
                      </TableCell>
                      <TableCell>{formatDateTime(run.started_at)}</TableCell>
                      <TableCell sx={{ maxWidth: 460 }}>
                        {run.error ? (
                          <Typography variant="caption" color="error" sx={{ whiteSpace: 'pre-wrap' }}>
                            {String(run.error)}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            {run.finished_at ? `Finalizó ${formatDateTime(run.finished_at)}` : '-'}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}

                  {!runs.length && (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Box py={3} textAlign="center">
                          <Typography variant="body2" color="text.secondary">
                            Sin ejecuciones registradas.
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenRuns(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
